import { vaultClient } from "../api/vaultClient";

const TEXT_ENCODER = new TextEncoder();
const TEXT_DECODER = new TextDecoder();
const SESSION_SECRET_KEY = "md_vault_sess_secret_v1";
const SESSION_BLOB_KEY = "md_vault_session_blob_v1";
const DEFAULT_ITER = 210000;

let state = {
  dek: null,
  unlocked: false,
  sessionBlob: "",
};

const getCrypto = () => {
  if (typeof window === "undefined") return null;
  return window.crypto?.subtle ? window.crypto : null;
};

const bytesToB64 = (bytes) => {
  const data = bytes instanceof ArrayBuffer ? new Uint8Array(bytes) : bytes;
  let binary = "";
  data.forEach((byte) => { binary += String.fromCharCode(byte); });
  return window.btoa(binary);
};

const b64ToBytes = (value) => {
  const binary = window.atob(String(value || ""));
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }
  return bytes;
};

const randomBytes = (length) => {
  const crypto = getCrypto();
  if (!crypto) throw new Error("crypto_not_available");
  return crypto.getRandomValues(new Uint8Array(length));
};

const aesParams = (iv, aad = "") => {
  const params = { name: "AES-GCM", iv };
  if (aad) params.additionalData = TEXT_ENCODER.encode(String(aad));
  return params;
};

const deriveKek = async (passphrase, saltB64, iter) => {
  const crypto = getCrypto();
  if (!crypto) throw new Error("crypto_not_available");
  const keyMaterial = await crypto.subtle.importKey(
    "raw",
    TEXT_ENCODER.encode(String(passphrase || "")),
    { name: "PBKDF2" },
    false,
    ["deriveKey"],
  );

  return crypto.subtle.deriveKey(
    {
      name: "PBKDF2",
      salt: b64ToBytes(saltB64),
      iterations: iter,
      hash: "SHA-256",
    },
    keyMaterial,
    { name: "AES-GCM", length: 256 },
    false,
    ["encrypt", "decrypt"],
  );
};

const importDek = async (raw) => {
  const crypto = getCrypto();
  if (!crypto) throw new Error("crypto_not_available");
  return crypto.subtle.importKey(
    "raw",
    raw,
    { name: "AES-GCM", length: 256 },
    false,
    ["encrypt", "decrypt"],
  );
};

const getSessionWrapKey = async () => {
  const crypto = getCrypto();
  if (!crypto) throw new Error("crypto_not_available");
  let secretB64 = "";
  try {
    secretB64 = window.sessionStorage.getItem(SESSION_SECRET_KEY) || "";
  } catch (_) {}

  if (!secretB64) {
    secretB64 = bytesToB64(randomBytes(32));
    try {
      window.sessionStorage.setItem(SESSION_SECRET_KEY, secretB64);
    } catch (_) {}
  }

  return crypto.subtle.importKey(
    "raw",
    b64ToBytes(secretB64),
    { name: "AES-GCM", length: 256 },
    false,
    ["encrypt", "decrypt"],
  );
};

const saveSessionBlob = (blob) => {
  state.sessionBlob = String(blob || "");
  try {
    if (state.sessionBlob) window.sessionStorage.setItem(SESSION_BLOB_KEY, state.sessionBlob);
    else window.sessionStorage.removeItem(SESSION_BLOB_KEY);
  } catch (_) {}
};

const exportSessionFromRawDek = async (dekRaw) => {
  const crypto = getCrypto();
  if (!crypto) throw new Error("crypto_not_available");
  const wrapKey = await getSessionWrapKey();
  const iv = randomBytes(12);
  const ct = await crypto.subtle.encrypt(aesParams(iv), wrapKey, dekRaw);
  return JSON.stringify({ v: 1, alg: "AES-GCM", iv: bytesToB64(iv), ct: bytesToB64(ct) });
};

const importSession = async (blob) => {
  const crypto = getCrypto();
  if (!crypto) throw new Error("crypto_not_available");
  let data = null;
  try {
    data = JSON.parse(String(blob || ""));
  } catch (_) {
    data = null;
  }
  if (!data || data.v !== 1 || data.alg !== "AES-GCM" || !data.iv || !data.ct) {
    throw new Error("bad_vault_session");
  }

  const wrapKey = await getSessionWrapKey();
  const dekRaw = await crypto.subtle.decrypt(
    aesParams(b64ToBytes(data.iv)),
    wrapKey,
    b64ToBytes(data.ct),
  );
  state.dek = await importDek(dekRaw);
  state.unlocked = true;
  saveSessionBlob(JSON.stringify(data));
  return { ok: true };
};

const restoreSession = async () => {
  if (state.unlocked && state.dek) return true;
  let blob = "";
  try {
    blob = window.sessionStorage.getItem(SESSION_BLOB_KEY) || "";
  } catch (_) {}
  if (!blob) return false;
  try {
    await importSession(blob);
    return true;
  } catch (_) {
    saveSessionBlob("");
    return false;
  }
};

const wrapRawDek = async (dekRaw, passphrase) => {
  const crypto = getCrypto();
  if (!crypto) throw new Error("crypto_not_available");
  const iter = DEFAULT_ITER;
  const salt = randomBytes(16);
  const saltB64 = bytesToB64(salt);
  const kek = await deriveKek(passphrase, saltB64, iter);
  const wrapIv = randomBytes(12);
  const wrapCt = await crypto.subtle.encrypt(aesParams(wrapIv), kek, dekRaw);

  return {
    v: 1,
    kdf: "PBKDF2",
    iter,
    salt: saltB64,
    wrap: {
      iv: bytesToB64(wrapIv),
      ct: bytesToB64(wrapCt),
    },
    alg: "AES-GCM",
  };
};

const unlockToRawDek = async (passphrase) => {
  const crypto = getCrypto();
  if (!crypto) throw new Error("crypto_not_available");
  const status = await vaultClient.status();
  if (!status?.hasVault || !status?.vault) throw new Error("vault_not_configured");
  const vault = status.vault;
  const kek = await deriveKek(passphrase, vault.salt, Number(vault.iter || 0));
  return crypto.subtle.decrypt(
    aesParams(b64ToBytes(vault.wrap?.iv)),
    kek,
    b64ToBytes(vault.wrap?.ct),
  );
};

const setup = async (passphrase) => {
  const crypto = getCrypto();
  if (!crypto) throw new Error("crypto_not_available");
  const dek = await crypto.subtle.generateKey(
    { name: "AES-GCM", length: 256 },
    true,
    ["encrypt", "decrypt"],
  );
  const dekRaw = await crypto.subtle.exportKey("raw", dek);
  const vaultPayload = await wrapRawDek(dekRaw, passphrase);

  await vaultClient.setup(vaultPayload);

  state.dek = await importDek(dekRaw);
  state.unlocked = true;
  saveSessionBlob(await exportSessionFromRawDek(dekRaw));
  return { ok: true };
};

const unlock = async (passphrase) => {
  const dekRaw = await unlockToRawDek(passphrase);
  state.dek = await importDek(dekRaw);
  state.unlocked = true;
  saveSessionBlob(await exportSessionFromRawDek(dekRaw));
  return { ok: true };
};

const rewrap = async ({ currentPassword, newPassword }) => {
  const dekRaw = await unlockToRawDek(currentPassword);
  const vault = await wrapRawDek(dekRaw, newPassword);
  state.dek = await importDek(dekRaw);
  state.unlocked = true;
  saveSessionBlob(await exportSessionFromRawDek(dekRaw));
  return { ok: true, vault };
};

const status = async () => {
  const restored = await restoreSession();
  const response = await vaultClient.status();
  return {
    ok: true,
    hasVault: Boolean(response?.hasVault || response?.has_vault),
    has_vault: Boolean(response?.hasVault || response?.has_vault),
    vault: response?.vault || null,
    unlocked: Boolean(restored || state.unlocked),
  };
};

const lock = () => {
  state = { dek: null, unlocked: false, sessionBlob: "" };
  saveSessionBlob("");
  return { ok: true };
};

const encryptText = async (text, aad = "") => {
  const crypto = getCrypto();
  if (!crypto) throw new Error("crypto_not_available");
  const restored = await restoreSession();
  if (!restored && (!state.unlocked || !state.dek)) throw new Error("vault_locked");
  const iv = randomBytes(12);
  const ct = await crypto.subtle.encrypt(
    aesParams(iv, aad),
    state.dek,
    TEXT_ENCODER.encode(String(text || "")),
  );
  return { iv: bytesToB64(iv), ct: bytesToB64(ct), ...(aad ? { aad } : {}) };
};

const decryptText = async (iv, ct, aad = "") => {
  const crypto = getCrypto();
  if (!crypto) throw new Error("crypto_not_available");
  const restored = await restoreSession();
  if (!restored && (!state.unlocked || !state.dek)) throw new Error("vault_locked");
  const pt = await crypto.subtle.decrypt(
    aesParams(b64ToBytes(iv), aad),
    state.dek,
    b64ToBytes(ct),
  );
  return TEXT_DECODER.decode(pt);
};

export const vaultEngine = {
  status,
  setup,
  unlock,
  rewrap,
  lock,
  encryptText,
  decryptText,
  _state: () => ({ unlocked: Boolean(state.unlocked && state.dek) }),
};
